/**
 * Converts a deserialized package/delivery object into a wizard draft format.
 * This allows the wizard to be pre-filled with existing data for a "Re-send" action.
 *
 * The deserialized package has:
 * - pkg.srcDstLocation: raw nested structure from API (has contactName/contactPhone per position)
 * - pkg.pickupLat/Lng/Address (flat fallback fields populated by serializer)
 * - pkg.destinationLat/Lng/Address (flat fallback fields)
 * - pkg.packageType: 'parcel' or 'document' (string)
 * - pkg.parcelDetails / pkg.weight / pkg.unit etc.
 * - pkg.deliveryType: 'moto' or 'car'
 *
 * @param {Object} pkg - The deserialized package object from the store
 * @returns {Object|null} - A draft object compatible with PackageDeliveryWizardContent.vue
 */
export function packageToWizardDraft(pkg) {
  if (!pkg) return null;

  // Use the raw nested srcDstLocation if present (contains contactName/contactPhone)
  const rawSrc = pkg.srcDstLocation?.srcPosition;
  const rawDst = pkg.srcDstLocation?.dstPosition;
  const rawCheckpoints = pkg.srcDstLocation?.checkpoints || pkg.checkpoints || [];

  /**
   * Normalize a value: return undefined if null/empty string (prevents "not allowed to be empty" API errors).
   * The API either requires a non-empty value or no value at all.
   */
  const normalize = (v) => (v === null || v === undefined || v === '') ? undefined : v;

  // Build srcPosition for the wizard — prefer the nested raw object
  const srcPosition = {
    coordinates: rawSrc?.coordinates || { lat: parseFloat(pkg.pickupLat), lng: parseFloat(pkg.pickupLng) },
    address: normalize(rawSrc?.address) || normalize(pkg.pickupAddress) || normalize(pkg.pickupLabel),
    locationText: normalize(rawSrc?.locationText) || normalize(rawSrc?.address) || normalize(pkg.pickupAddress) || normalize(pkg.pickupLabel),
    city: normalize(rawSrc?.city) || normalize(pkg.pickupCity) || normalize(pkg.pickup?.city),
    description: normalize(rawSrc?.description) || normalize(pkg.pickupDescription) || normalize(pkg.pickup?.description),
    contactName: normalize(rawSrc?.contactName) || normalize(pkg.pickup?.contactName),
    contactPhone: normalize(rawSrc?.contactPhone) || normalize(pkg.pickup?.phone)
  };

  // Build dstPosition for the wizard — prefer the nested raw object
  const dstPosition = {
    coordinates: rawDst?.coordinates || { lat: parseFloat(pkg.destinationLat ?? pkg.destination?.latitude ?? pkg.destination?.coordinates?.lat), lng: parseFloat(pkg.destinationLng ?? pkg.destination?.longitude ?? pkg.destination?.coordinates?.lng) },
    address: normalize(rawDst?.address) || normalize(pkg.destinationAddress) || normalize(pkg.destinationLabel) || normalize(pkg.destination?.address),
    locationText: normalize(rawDst?.locationText) || normalize(rawDst?.address) || normalize(pkg.destinationAddress) || normalize(pkg.destinationLabel),
    city: normalize(rawDst?.city) || normalize(pkg.destinationCity) || normalize(pkg.destination?.city),
    description: normalize(rawDst?.description) || normalize(pkg.destinationDescription) || normalize(pkg.destination?.description),
    contactName: normalize(rawDst?.contactName) || normalize(pkg.destination?.contactName),
    contactPhone: normalize(rawDst?.contactPhone) || normalize(pkg.destination?.phone)
  };

  // Map checkpoints
  const checkpoints = rawCheckpoints.map(cp => {
    if (cp?.coordinates) {
      return {
        coordinates: cp.coordinates,
        address: cp.address,
        locationText: cp.locationText || cp.address,
        city: cp.city,
        description: cp.description,
        contactName: normalize(cp.contactName),
        contactPhone: normalize(cp.contactPhone)
      };
    }
    return {
      coordinates: { lat: parseFloat(cp.lat), lng: parseFloat(cp.lng) },
      address: cp.address || cp.label,
      locationText: cp.address || cp.label,
      city: cp.city,
      description: cp.description,
      contactName: normalize(cp.contactName),
      contactPhone: normalize(cp.contactPhone)
    };
  }).filter(cp => cp.coordinates?.lat && cp.coordinates?.lng);

  // Resolve parcel details — try pkg.parcelDetails first (from API), then flat fields
  const rawParcel = pkg.parcelDetails || pkg.packageType?.parcelDetails;
  const parcelDetails = {
    unit: rawParcel?.unit || pkg.unit || 'kg',
    weight: rawParcel?.weight ?? pkg.weight ?? 1,
    length: rawParcel?.length ?? pkg.length ?? 1,
    width: rawParcel?.width ?? pkg.width ?? 1,
    height: rawParcel?.height ?? pkg.height ?? 1,
    quantity: rawParcel?.quantity ?? pkg.quantity ?? 1
  };

  // Resolve package type string
  const packageTypeStr = typeof pkg.packageType === 'string'
    ? pkg.packageType
    : (pkg.packageType?.type || 'parcel');

  // startDate must be a Date object (PickDateStep stores/restores a Date or timestamp)
  // Use current time + 30min for a re-send
  const startDateObj = new Date(Date.now() + 30 * 60 * 1000);

  const results = {
    // Step 1: Start Date — must be a Date object
    startDate: startDateObj,

    // Step 2: Locations
    srcDstLocation: {
      srcPosition,
      dstPosition,
      checkpoints
    },

    // Step 3: Package Type
    packageType: {
      type: packageTypeStr,
      nbDocs: pkg.nbDocs || 1,
      parcelDetails
    },

    // Step 4: Delivery Type
    deliveryType: {
      deliveryType: typeof pkg.deliveryType === 'string'
        ? pkg.deliveryType
        : (pkg.deliveryType?.deliveryType || 'moto')
    }
  };

  return {
    id: `resend_${pkg.id || Date.now()}`,
    step: 2, // Start at step 2 so user can review/complete location contact info
    results,
    isResend: true
  };
}
